import { Box, Stack, TableCell, Tooltip, Typography } from '@mui/material';
import IconButton from 'src/components/IconButton';
import Iconify from 'src/components/Iconify';
import StatusChip from 'src/components/StatusChip';
import TableHeadTypography from 'src/components/TableHeadTypography';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { getBasePolicyModeColor, getBasePolicyModeLabel } from 'src/utils/enums/basePolicyMode';
import { getWdacPolicyPurposeLabel } from 'src/utils/enums/wdacPolicyPurpose';
import { WDAC_POLICY_TYPE, getWdacPolicyTypeLabel } from 'src/utils/enums/wdacPolicyType';

// What the two detected views share is how a policy row reads, not how either one asks for its
// rows. Paging, sorting and the filter set belong to each table so they can diverge.
export const POLICY_TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': { fontSize: '0.8125rem' },
};

const MANAGED_CHIP_COLOR = '#87C8E0';

const isBasePolicy = policy => policy.policyType === WDAC_POLICY_TYPE.BasePolicy;

// A yes/no/either filter over a boolean field is all strings, because the filter controls, the
// chips and the URL all deal in strings and an unset filter has to stay distinguishable from an
// explicit false. Empty means the filter is not being applied, so it drops out of the request.
export const toBooleanParam = value => (value ? value === 'true' : undefined);

export const PolicyHeaderCell = ({ column, sort, onSort }) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();
  const isSorted = sort.column === column.sortKey;

  return (
    <TableCell sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
      <Stack
        direction="row"
        gap={0.5}
        alignItems="center"
        onClick={column.sortKey ? () => onSort(column) : undefined}
        sx={{ cursor: column.sortKey ? 'pointer' : 'default' }}>
        <TableHeadTypography
          hasSorting={isSorted}
          mediumScreen={mediumScreen}
          largeScreen={largeScreen}
          smallScreen={smallScreen}>
          {column.label}
        </TableHeadTypography>
        {column.sortKey ? (
          <Iconify
            icon={
              isSorted
                ? sort.direction === 'asc'
                  ? 'mdi:arrow-up'
                  : 'mdi:arrow-down'
                : 'mdi:swap-vertical'
            }
            width={14}
            height={14}
            color={isSorted ? '#7DFF92' : '#9CA3AF'}
          />
        ) : null}
      </Stack>
    </TableCell>
  );
};

// The two row-level switches sit together because they answer the same question about a policy's
// standing. Only a base policy can be managed - a supplemental is managed by whatever generated it
// - so a supplemental keeps the star's slot rather than sliding its eye under the stars above it.
export const PolicyActionsCell = ({
  policy,
  managedDisabled,
  hiddenDisabled,
  onToggleManaged,
  onToggleHidden,
}) => {
  const isBase = isBasePolicy(policy);
  // A managed supplemental is a component the portal generated, so it stays visible; a managed base
  // can be hidden, which unmanages it, so long as nothing generated is still attached.
  const cannotHide = !isBase && !policy.isHidden && policy.isPortalManaged;

  return (
    <TableCell onClick={event => event.stopPropagation()} sx={{ py: 0.75 }}>
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Box sx={{ visibility: isBase ? 'visible' : 'hidden' }} aria-hidden={!isBase}>
          <Tooltip
            title={
              policy.isHidden
                ? 'A hidden policy cannot be managed by the portal.'
                : policy.isPortalManaged
                  ? 'Stop managing this base policy'
                  : 'Manage this base policy'
            }>
            <span>
              <IconButton
                disabled={!isBase || policy.isHidden || managedDisabled}
                onClick={() => onToggleManaged(policy)}>
                <Iconify
                  icon={policy.isPortalManaged ? 'mdi:star' : 'mdi:star-outline'}
                  color={
                    policy.isHidden ? '#727272' : policy.isPortalManaged ? '#7DFF92' : '#9CA3AF'
                  }
                  width={20}
                  height={20}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Tooltip
          title={
            cannotHide
              ? 'A supplemental policy generated by the portal cannot be hidden.'
              : policy.isHidden
                ? 'Show policy'
                : 'Hide policy'
          }>
          <span>
            <IconButton
              disabled={cannotHide || hiddenDisabled}
              onClick={() => onToggleHidden(policy)}>
              <Iconify
                icon={policy.isHidden ? 'mdi:eye-off-outline' : 'octicon:eye-24'}
                color={cannotHide ? '#727272' : '#7DFF92'}
                width={20}
                height={20}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </TableCell>
  );
};

export const PolicyNameCell = ({ policyName, policyId, onOpen }) => (
  <TableCell sx={{ py: 0.75, minWidth: 0 }}>
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        noWrap
        onClick={
          onOpen
            ? event => {
                event.stopPropagation();
                onOpen();
              }
            : undefined
        }
        sx={{
          fontWeight: 600,
          ...(onOpen ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}),
        }}>
        {policyName}
      </Typography>
      <Typography variant="caption" noWrap sx={{ color: '#9CA3AF' }}>
        {policyId}
      </Typography>
    </Stack>
  </TableCell>
);

export const PolicyTypeCell = ({ policyType, basePolicyMode }) => {
  const modeColor = getBasePolicyModeColor(basePolicyMode);

  return (
    <TableCell sx={{ py: 0.75 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
        <Typography variant="caption">{getWdacPolicyTypeLabel(policyType) || '—'}</Typography>
        {modeColor ? (
          <StatusChip
            label={getBasePolicyModeLabel(basePolicyMode)}
            color={modeColor}
            backgroundColor={`${modeColor}1F`}
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        ) : null}
      </Stack>
    </TableCell>
  );
};

export const PolicyCaptionCell = ({ children, color, ...props }) => (
  <TableCell sx={{ py: 0.75 }} {...props}>
    <Typography variant="caption" sx={color ? { color } : undefined}>
      {children}
    </Typography>
  </TableCell>
);

// A base policy's managed state is toggled from the star, and a supplemental's is inherited from
// whatever generated it, so this column only reports. The purpose reads as a footnote to the yes,
// so it is set smaller than the mode chip that has to carry its column on its own.
export const PolicyManagedCell = ({ policy }) => (
  <TableCell sx={{ py: 0.75 }}>
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
      <Typography variant="caption" sx={{ color: '#D1D5DB' }}>
        {policy.isPortalManaged ? 'Yes' : 'No'}
      </Typography>
      {policy.isPortalManaged && policy.policyPurpose ? (
        <StatusChip
          label={getWdacPolicyPurposeLabel(policy.policyPurpose)}
          color={MANAGED_CHIP_COLOR}
          backgroundColor={`${MANAGED_CHIP_COLOR}1F`}
          sx={{
            height: 17,
            fontSize: '0.58rem',
            '& .MuiChip-label': { px: 0.65 },
          }}
        />
      ) : null}
    </Stack>
  </TableCell>
);
